import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getMySchedule } from '../../services/shiftScheduleService';
import Tooltip from '../../components/ui/Tooltip';
import SwapRequestModal from './SwapRequestModal';

const monthNames = {
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  kk: ['Қаңтар', 'Ақпан', 'Наурыз', 'Сәуір', 'Мамыр', 'Маусым', 'Шілде', 'Тамыз', 'Қыркүйек', 'Қазан', 'Қараша', 'Желтоқсан'],
  az: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun', 'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
};

const dayHeaders = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = (firstDay.getDay() + 6) % 7;
  const days = [];
  for (let i = startPadding - 1; i >= 0; i--)
    days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
  for (let d = 1; d <= lastDay.getDate(); d++)
    days.push({ date: new Date(year, month, d), isCurrentMonth: true });
  let nextDay = 1;
  while (days.length % 7 !== 0)
    days.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
  return days;
};

export default function MySchedulePage() {
  const { i18n } = useTranslation();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [scheduleMap, setScheduleMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  // Nullable context for the swap-request modal — set when a swap-eligible
  // cell (strictly-future date, has a work hour) is clicked.
  const [swapCtx, setSwapCtx] = useState(null);

  const calendarDays = useMemo(() => buildCalendarDays(year, month), [year, month]);

  const fetchSchedule = useCallback(async () => {
    const firstDate = calendarDays[0].date;
    const lastDate = calendarDays[calendarDays.length - 1].date;
    setLoading(true);
    try {
      const res = await getMySchedule({
        startDate: formatDate(firstDate),
        endDate: formatDate(lastDate),
      });
      const map = new Map();
      const items = res?.data?.data || res?.data || [];
      items.forEach((item) => {
        const key = item.date?.substring(0, 10);
        if (key) map.set(key, item);
      });
      setScheduleMap(map);
    } catch {
      setScheduleMap(new Map());
    } finally {
      setLoading(false);
    }
  }, [calendarDays]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handlePrev = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const handleNext = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleToday = () => {
    const now = new Date();
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  const lang = i18n.language?.substring(0, 2) || 'tr';
  const months = monthNames[lang] || monthNames.tr;
  const todayStr = formatDate(today);

  const getCellInfo = (day) => {
    const key = formatDate(day.date);
    const entry = scheduleMap.get(key);
    const isToday = key === todayStr;
    const dayOfWeek = day.date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    let bg = '#fff';
    let content = null;
    let tooltipText = null;

    if (entry) {
      if (entry.isHoliday) {
        bg = '#fef9c3';
        content = <HolidayText>{entry.holidayName}</HolidayText>;
        tooltipText = entry.holidayName;
      } else if (entry.workHourId) {
        bg = (entry.workHourColorCode || '#0291EF') + '20';
        content = (
          <>
            <ShortCode $color={entry.workHourColorCode}>{entry.workHourShortCode}</ShortCode>
            <WorkHourName>{entry.workHourName}</WorkHourName>
          </>
        );
        const start = entry.workHourStart?.substring(0, 5) || '';
        const end = entry.workHourEnd?.substring(0, 5) || '';
        tooltipText = `${entry.workHourName}\n${start} - ${end}`;
      } else {
        bg = isWeekend ? '#f8fafc' : '#f1f5f9';
        content = <OffText>OFF</OffText>;
      }
    } else {
      bg = isWeekend ? '#f8fafc' : '#f1f5f9';
      if (day.isCurrentMonth && !loading) {
        content = <OffText>OFF</OffText>;
      }
    }

    return { bg, content, isToday, isWeekend, tooltipText };
  };

  return (
    <Container>
      <Header>
        <NavSection>
          <NavButton onClick={handlePrev}>&#9664;</NavButton>
          <MonthLabel>{months[month]} {year}</MonthLabel>
          <NavButton onClick={handleNext}>&#9654;</NavButton>
        </NavSection>
        <TodayButton onClick={handleToday}>Bugün</TodayButton>
      </Header>

      <CalendarGrid>
        {dayHeaders.map((dh) => (
          <DayHeader key={dh}>{dh}</DayHeader>
        ))}

        {calendarDays.map((day) => {
          const key = formatDate(day.date);
          const entry = scheduleMap.get(key);
          const { bg, content, isToday, tooltipText } = getCellInfo(day);
          // Swap-eligible: strictly-future published/completed shift with a
          // work hour AND a real ShiftSchedule row (entry.id can be null for
          // work-plan-fallback "virtual" days — those have no shift to swap).
          const cellClickable = key > todayStr && !!entry?.workHourId && !!entry?.id;

          const cell = (
            <DayCell
              key={key}
              $bg={bg}
              $outOfMonth={!day.isCurrentMonth}
              $isToday={isToday}
              $clickable={cellClickable}
              onClick={cellClickable
                ? () => setSwapCtx({ shiftId: entry.id, date: key, workHourName: entry.workHourName })
                : undefined}
            >
              <DayNumber $isToday={isToday}>{day.date.getDate()}</DayNumber>
              {content}
            </DayCell>
          );

          if (tooltipText && day.isCurrentMonth) {
            return (
              <Tooltip key={key} text={tooltipText} position="bottom">
                {cell}
              </Tooltip>
            );
          }

          return cell;
        })}
      </CalendarGrid>

      <SwapRequestModal
        isOpen={!!swapCtx}
        ctx={swapCtx}
        onClose={() => setSwapCtx(null)}
        onCreated={fetchSchedule}
      />
    </Container>
  );
}

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NavButton = styled.button`
  background: none;
  border: 1px solid ${p => p.theme.colors?.border || '#E8ECF0'};
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 14px;
  color: ${p => p.theme.colors?.text?.primary || '#004242'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${p => p.theme.colors?.bgColor || '#F5F7FA'};
  }
`;

const MonthLabel = styled.span`
  font-size: ${p => p.theme.metrics?.font?.size?.lg || '18px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.bold || 700};
  color: ${p => p.theme.colors?.text?.primary || '#004242'};
  min-width: 180px;
  text-align: center;
`;

const TodayButton = styled.button`
  background: ${p => p.theme.colors?.primary?.main || '#0291EF'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: ${p => p.theme.metrics?.font?.size?.sm || '14px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.semiBold || 600};
  font-family: ${p => p.theme.metrics?.font?.family || "'Nunito Sans', sans-serif"};

  &:hover {
    opacity: 0.9;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #E8ECF0;
  border-radius: 6px;
  overflow: hidden;
`;

const DayHeader = styled.div`
  background: ${p => p.theme.colors?.bgColor || '#F5F7FA'};
  padding: 10px;
  text-align: center;
  font-size: ${p => p.theme.metrics?.font?.size?.sm || '13px'};
  font-weight: ${p => p.theme.metrics?.font?.weight?.semiBold || 600};
  color: ${p => p.theme.colors?.text?.secondary || '#64748b'};
`;

const DayCell = styled.div`
  background: ${p => p.$bg || '#fff'};
  min-height: 90px;
  padding: 8px;
  opacity: ${p => (p.$outOfMonth ? 0.35 : 1)};
  border: ${p => (p.$isToday ? '2px solid #0291EF' : 'none')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};

  ${p => p.$clickable && `
    &:hover {
      box-shadow: inset 0 0 0 2px rgba(2, 145, 239, 0.35);
    }
  `}
`;

const DayNumber = styled.span`
  font-size: 13px;
  font-weight: ${p => (p.$isToday ? 700 : 400)};
  color: ${p => (p.$isToday ? '#EF4444' : '#334155')};
  align-self: flex-end;
`;

const ShortCode = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.$color || '#0291EF'};
  margin-top: 4px;
`;

const WorkHourName = styled.span`
  font-size: 11px;
  color: #64748b;
  text-align: center;
  line-height: 1.2;
`;

const OffText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 8px;
`;

const HolidayText = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #b45309;
  text-align: center;
  margin-top: 4px;
  line-height: 1.2;
`;
